import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { getStockTrades, StockTrade, getStockEngines, StockEngine } from '../../services/ttStockTraderService';
import '../Dashboard.css';

const c = {
  bg: '#0f172a', card: '#1f2937', cardAlt: '#1a2332', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  green: '#16a34a', greenBg: '#16a34a20', red: '#dc2626', redBg: '#dc262620',
  amber: '#d97706', amberBg: '#d9770620', purple: '#8b5cf6', purpleBg: '#8b5cf620',
};

const fmtMoney = (n: number | null | undefined) => {
  if (n == null) return '—';
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtPct = (n: number | null | undefined) => n == null ? '—' : `${n.toFixed(1)}%`;
const colorFor = (n: number | null | undefined) => n == null ? c.muted : n > 0 ? c.green : n < 0 ? c.red : c.text;

const STATUS_COLOR: Record<string, string> = {
  placing: c.amber, submitted: c.amber, open: c.green, closed: c.dim, cancelled: c.muted, failed: c.red,
};

// Compact label + tooltip per equity source so the chip explains itself.
const EQUITY_SOURCE_META: Record<string, { label: string; color: string; bg: string; long: string }> = {
  paper_form: { label: 'FORM', color: c.amber, bg: c.amberBg, long: 'Paper — sized off the engine\'s configured starting equity' },
  snapshot: { label: 'SNAPSHOT', color: c.purple, bg: c.purpleBg, long: 'Live — sized off equityBP from the latest tt-account-snapshot (≤60 min old)' },
  tt_live_refresh: { label: 'TT LIVE', color: c.green, bg: c.greenBg, long: 'Live — snapshot was stale, fetched equityBP directly from TT at submit time' },
  live_no_tt_connection: { label: 'ERR: NO CONN', color: c.red, bg: c.redBg, long: 'Live engine had no TT credentials at submit time — should not happen for a filled trade' },
  live_refresh_failed: { label: 'ERR: NO DATA', color: c.red, bg: c.redBg, long: 'Both snapshot and live TT refresh failed — should not happen for a filled trade' },
};

const KPI: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, padding: '0.7rem 0.85rem' }}>
    <div style={{ fontSize: '0.65rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
    <div style={{ marginTop: 4, fontSize: '1.1rem', fontWeight: 700, color: valueColor || c.text }}>{value}</div>
  </div>
);

const Cell: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div>
    <div style={{ fontSize: '0.62rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    <div style={{ fontWeight: 600, color: valueColor || c.text }}>{value}</div>
  </div>
);

const TradeCard: React.FC<{ trade: StockTrade; engineName?: string; expanded: boolean; onToggle: () => void }> = ({ trade, engineName, expanded, onToggle }) => {
  const isClosed = trade.status === 'closed';
  const pnl = isClosed ? trade.pnl : trade.unrealizedPnl;
  const sideTag = trade.side === 'buy' ? '↑' : '↓';
  const sideColor = trade.side === 'buy' ? c.green : c.red;

  return (
    <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6 }}>
      <div onClick={onToggle} style={{
        cursor: 'pointer', padding: '0.7rem 0.9rem',
        display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) minmax(220px, 2fr) minmax(120px, 1fr) auto',
        gap: '0.75rem', alignItems: 'center',
      }}>
        <div>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: c.text }}>{trade.symbol}</span>
          <span style={{ marginLeft: 6, fontSize: '0.85rem', color: sideColor, fontWeight: 700 }}>{sideTag}</span>
          <span style={{
            marginLeft: 8, fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
            padding: '1px 6px', borderRadius: 3,
            background: trade.paperMode ? c.amberBg : c.greenBg,
            color: trade.paperMode ? c.amber : c.green,
          }}>{trade.paperMode ? 'PAPER' : 'LIVE'}</span>
          {trade.entryEquitySource && EQUITY_SOURCE_META[trade.entryEquitySource] && (
            <span
              title={EQUITY_SOURCE_META[trade.entryEquitySource].long}
              style={{
                marginLeft: 4, fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                padding: '1px 6px', borderRadius: 3,
                background: EQUITY_SOURCE_META[trade.entryEquitySource].bg,
                color: EQUITY_SOURCE_META[trade.entryEquitySource].color,
              }}
            >{EQUITY_SOURCE_META[trade.entryEquitySource].label}</span>
          )}
          <div style={{ fontSize: '0.7rem', color: c.muted, marginTop: 2 }}>{trade.strategy_name || trade.strategy}</div>
          {engineName && <div style={{ fontSize: '0.65rem', color: c.dim, marginTop: 1 }}>{engineName}</div>}
        </div>
        <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap', fontSize: '0.82rem' }}>
          <Cell label="Qty" value={String(trade.qty)} />
          <Cell label="Entry" value={fmtMoney(trade.filledPrice)} />
          <Cell label={isClosed ? 'Exit' : 'Mark'} value={fmtMoney(isClosed ? trade.exitPrice : trade.currentPrice)} />
          {!isClosed && <Cell label="Stop" value={fmtMoney(trade.trailingStop ?? trade.suggestedStop)} />}
          {isClosed && <Cell label="Reason" value={trade.exitReason || '—'} valueColor={c.muted} />}
          <Cell label="Status" value={trade.status} valueColor={STATUS_COLOR[trade.status] || c.text} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.62rem', color: c.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{isClosed ? 'P&L' : 'Unrealized'}</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: colorFor(pnl) }}>{fmtMoney(pnl)}</div>
        </div>
        <div style={{ fontSize: '0.78rem', color: c.muted }}>{expanded ? '▼' : '▶'}</div>
      </div>
      {expanded && (
        <div style={{ padding: '0 0.9rem 0.85rem', borderTop: `1px solid ${c.border}`, background: c.cardAlt }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.6rem', fontSize: '0.78rem' }}>
            <Cell label="Stop" value={fmtMoney(trade.suggestedStop)} />
            <Cell label="Target" value={fmtMoney(trade.suggestedTarget)} />
            <Cell label="Base score" value={String(trade.base_score ?? '—')} />
            <Cell label="Final score" value={String(trade.score ?? '—')} />
            <Cell label="Regime mult" value={trade.regime_multiplier?.toFixed(3) || '—'} />
            <Cell label="Entry at" value={(trade.entryAt || trade.placingAt || '').slice(0, 16).replace('T', ' ')} />
            {isClosed && <Cell label="Exit at" value={(trade.exitAt || '').slice(0, 16).replace('T', ' ')} />}
            {trade.ttOrderId && <Cell label="TT order" value={String(trade.ttOrderId)} />}
            {trade.failureReason && <Cell label="Failure" value={trade.failureReason} valueColor={c.red} />}
          </div>
          {/* Sizing audit: where did the entry equity come from? */}
          {trade.entryEquitySource && (
            <div style={{ marginTop: '0.7rem', padding: '0.45rem 0.7rem', background: c.bg, borderRadius: 4, fontSize: '0.75rem', color: c.muted, lineHeight: 1.5 }}>
              <div style={{ color: c.dim, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Sizing</div>
              <div>
                Source: <span style={{ color: EQUITY_SOURCE_META[trade.entryEquitySource]?.color || c.text, fontWeight: 600 }}>{EQUITY_SOURCE_META[trade.entryEquitySource]?.label || trade.entryEquitySource}</span>
                <span style={{ marginLeft: 8, color: c.dim }}>{EQUITY_SOURCE_META[trade.entryEquitySource]?.long}</span>
              </div>
              <div style={{ marginTop: 3, display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                {trade.entryEquity != null && <span>Sizing base: <strong style={{ color: c.text }}>${trade.entryEquity.toLocaleString('en-US', { maximumFractionDigits: 0 })}</strong></span>}
                {trade.entryNetLiq != null && <span>NetLiq @ entry: <strong style={{ color: c.text }}>${trade.entryNetLiq.toLocaleString('en-US', { maximumFractionDigits: 0 })}</strong></span>}
                {trade.entryAccountNumber && <span>Account: <strong style={{ color: c.text }}>{trade.entryAccountNumber}</strong></span>}
              </div>
            </div>
          )}
          {trade.regime_adjustments && trade.regime_adjustments.length > 0 && (
            <div style={{ marginTop: '0.7rem' }}>
              <div style={{ fontSize: '0.7rem', color: c.muted, fontWeight: 600, marginBottom: 4 }}>Regime overlay</div>
              {trade.regime_adjustments.map((a, i) => (
                <div key={i} style={{ fontSize: '0.78rem', color: c.text }}>
                  <span style={{ display: 'inline-block', width: 60, color: c.muted }}>{a.factor}:</span>
                  <span style={{ color: a.value > 0 ? c.green : c.red, fontWeight: 600 }}>{a.value > 0 ? '+' : ''}{a.value}</span>
                  <span style={{ color: c.muted, marginLeft: '0.5rem' }}>{a.reason}</span>
                </div>
              ))}
            </div>
          )}
          {trade.rationale && (
            <div style={{ marginTop: '0.7rem', padding: '0.5rem 0.7rem', background: c.bg, borderRadius: 4, fontSize: '0.78rem', whiteSpace: 'pre-wrap', color: c.muted, lineHeight: 1.5 }}>
              {trade.rationale}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TtStockTrades: React.FC = () => {
  const { token } = useAuth();
  const [trades, setTrades] = useState<StockTrade[]>([]);
  const [engines, setEngines] = useState<StockEngine[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [engineFilter, setEngineFilter] = useState<string>('all');
  const [paperFilter, setPaperFilter] = useState<'all' | 'paper' | 'live'>('all');

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true); setErr(null);
      const [t, e] = await Promise.all([getStockTrades(token, { limit: 200 }), getStockEngines(token)]);
      setTrades(t); setEngines(e);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load trades');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const engineName = (engineId: string) => engines.find((e) => e.engineId === engineId)?.name || engineId.slice(0, 8);

  const filtered = useMemo(() => trades.filter((t) => {
    if (engineFilter !== 'all' && t.engineId !== engineFilter) return false;
    if (paperFilter === 'paper' && !t.paperMode) return false;
    if (paperFilter === 'live' && t.paperMode) return false;
    return true;
  }), [trades, engineFilter, paperFilter]);

  const open = filtered.filter((t) => t.status === 'open' || t.status === 'submitted' || t.status === 'placing');
  const closed = filtered.filter((t) => t.status === 'closed');
  const failed = filtered.filter((t) => t.status === 'failed' || t.status === 'cancelled');

  const realized = closed.reduce((s, t) => s + (t.pnl || 0), 0);
  const unrealized = open.reduce((s, t) => s + (t.unrealizedPnl || 0), 0);
  const wins = closed.filter((t) => (t.pnl || 0) > 0);
  const winRate = closed.length > 0 ? (wins.length / closed.length) * 100 : null;

  const toggleRow = (id: string) => {
    setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  if (loading) return <div className="dashboard-container"><Navigation /><div className="dashboard-content" style={{ padding: 24, color: c.muted }}>Loading…</div></div>;

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content" style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 24px', color: c.text }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>TT Stocks — Trades</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: c.muted }}>All TT equity trades across your engines (paper + live).</p>
          </div>
          <button onClick={load} style={{ padding: '6px 14px', background: c.purple, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>↻ Refresh</button>
        </div>

        {err && <div style={{ background: c.redBg, border: `1px solid ${c.red}`, color: '#fca5a5', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: c.muted }}>Engine:&nbsp;
            <select value={engineFilter} onChange={(e) => setEngineFilter(e.target.value)} style={{ background: '#111827', color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: '4px 8px' }}>
              <option value="all">All</option>
              {engines.map((e) => <option key={e.engineId} value={e.engineId}>{e.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: c.muted }}>Mode:&nbsp;
            <select value={paperFilter} onChange={(e) => setPaperFilter(e.target.value as any)} style={{ background: '#111827', color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: '4px 8px' }}>
              <option value="all">All</option>
              <option value="paper">Paper</option>
              <option value="live">Live</option>
            </select>
          </label>
        </div>

        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
          <KPI label="Open" value={String(open.length)} />
          <KPI label="Closed" value={String(closed.length)} />
          <KPI label="Win rate" value={fmtPct(winRate)} />
          <KPI label="Realized P&L" value={fmtMoney(realized)} valueColor={colorFor(realized)} />
          <KPI label="Unrealized" value={fmtMoney(unrealized)} valueColor={colorFor(unrealized)} />
          {failed.length > 0 && <KPI label="Failed/Cancelled" value={String(failed.length)} valueColor={c.red} />}
        </div>

        {/* Open + submitted + placing */}
        <section style={{ marginBottom: 16 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 700 }}>Open ({open.length})</h2>
          {open.length === 0
            ? <div style={{ color: c.muted, fontSize: 14, padding: '0.75rem', background: c.cardAlt, borderRadius: 6 }}>No open positions.</div>
            : <div style={{ display: 'grid', gap: 6 }}>{open.map((t) => <TradeCard key={t.tradeId} trade={t} engineName={engineName(t.engineId)} expanded={expanded.has(t.tradeId)} onToggle={() => toggleRow(t.tradeId)} />)}</div>}
        </section>

        {/* Closed */}
        <section style={{ marginBottom: 16 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 700 }}>Closed ({closed.length})</h2>
          {closed.length === 0
            ? <div style={{ color: c.muted, fontSize: 14, padding: '0.75rem', background: c.cardAlt, borderRadius: 6 }}>No closed trades yet.</div>
            : <div style={{ display: 'grid', gap: 6 }}>{closed.slice(0, 50).map((t) => <TradeCard key={t.tradeId} trade={t} engineName={engineName(t.engineId)} expanded={expanded.has(t.tradeId)} onToggle={() => toggleRow(t.tradeId)} />)}</div>}
        </section>

        {/* Failed + cancelled */}
        {failed.length > 0 && (
          <section style={{ marginBottom: 16 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 700, color: c.red }}>Failed / Cancelled ({failed.length})</h2>
            <div style={{ display: 'grid', gap: 6 }}>{failed.map((t) => <TradeCard key={t.tradeId} trade={t} engineName={engineName(t.engineId)} expanded={expanded.has(t.tradeId)} onToggle={() => toggleRow(t.tradeId)} />)}</div>
          </section>
        )}
      </div>
    </div>
  );
};

export default TtStockTrades;
